import { BaseBadge, Card, FeaturedIcon } from '@/lib';
import { PerformanceAudienceStoreVisits as StoreVisitsData } from '@/services/endpoints/performanceAudience';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LuStore } from 'react-icons/lu';
import { usePerformanceAudienceFormatters } from '../../hooks/usePerformanceAudienceFormatters';
type PerformanceAudienceStoreVisitsProps = {
  storeVisits: StoreVisitsData;
};

export const PerformanceAudienceStoreVisits: FC<PerformanceAudienceStoreVisitsProps> = ({ storeVisits }) => {
  const { t } = useTranslation('performanceAudience');
  const { formatNumber } = usePerformanceAudienceFormatters();

  return (
    <Card className='flex flex-col gap-6 border-primary-border bg-white p-6 drop-shadow-none'>
      <header className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon color='brand' size='md' type='modern'>
            <LuStore className='size-5' aria-hidden />
          </FeaturedIcon>
          <div>
            <h2 className='text-base font-semibold text-primary-900'>{t('storeVisits.title')}</h2>
            <p className='text-sm text-tertiary-600'>{t('storeVisits.subtitle', { count: storeVisits.locationCount })}</p>
          </div>
        </div>
        {storeVisits.status === 'active' ? (
          <BaseBadge className='bg-green-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-green-700'>
            {t('storeVisits.active')}
          </BaseBadge>
        ) : null}
      </header>

      <div className='grid gap-6 lg:grid-cols-[minmax(12rem,1fr)_2fr] lg:items-center'>
        <div className='grid gap-1'>
          <p className='text-4xl font-semibold text-primary-900'>{formatNumber(storeVisits.total)}</p>
          <p className='text-sm text-tertiary-600'>{t('storeVisits.totalLabel')}</p>
        </div>

        <div className='grid gap-4 sm:grid-cols-3'>
          {storeVisits.locations.map((location) => (
            <Card
              key={location.id}
              className='flex flex-col gap-3 border-primary-border bg-gray-50 p-4 drop-shadow-none'
            >
              <div className='flex items-center justify-between gap-2'>
                <p className='text-sm font-semibold text-primary-900'>{location.name}</p>
                <p className='text-lg font-semibold text-brand-700'>{formatNumber(location.visits)}</p>
              </div>
              <div className='bg-primary-200 h-1.5 overflow-hidden rounded-full'>
                <div
                  className='h-full rounded-full bg-brand-600'
                  style={{ width: `${location.share}%` }}
                  role='progressbar'
                  aria-valuenow={location.share}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={location.name}
                />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Card>
  );
};
